import { Component, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { KorviuListComponent, KorviuLoaderComponent } from 'shared';
import { finalize, Observable } from 'rxjs';
import { ICategory } from 'core/types/category.interface';
import { CategoriesService } from 'core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { IBrand } from 'core/types/brand.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { CategoryCarouselComponent } from '../category-carousel/category-carousel.component';
import { createBrandSortSections } from '../reward-list-options';

@Component({
  selector: 'app-brands',
  imports: [KorviuLoaderComponent, KorviuListComponent, BreadcrumbComponent, CategoryCarouselComponent],
  templateUrl: './brands.component.html',
  styleUrl: './brands.component.scss'
})
export class BrandsComponent implements OnInit {
  readonly #categoriesService = inject(CategoriesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);

  readonly categoriesIsLoading = signal(false);
  readonly selectedCategoryId = signal<number | null>(null);
  readonly categories = signal<ICategory[]>([]);

  readonly brandList = viewChild<KorviuListComponent>('brandList');

  readonly brandListConfig: IKorviuListConfig = {
    getFn: this.getBrands.bind(this),
    sortSections: createBrandSortSections(),
    filterSections: [],
    hideFilterSection: true,
    itemType: KorviuListItemTypes.BRAND,
    totalLocalizableName: 'rewards.brands',
    pagination: { pageSize: 24, page: 1 },
    scrollPagination: true,
    detailsFn: this.onBrandClick.bind(this)
  };

  ngOnInit(): void {
    this.#loadCategories();
  }

  getBrands(
    pagination: IPagination,
    filters?: IFilter[],
    sorting?: ISorting
  ): Observable<IGetPaginatedResponse<IBrand[]>> {
    const categoryId = this.selectedCategoryId();
    if (categoryId !== null) {
      return this.#categoriesService.getBrands(categoryId, pagination, filters, sorting);
    }
    return this.#categoriesService.getAllBrands(pagination, filters, sorting);
  }

  onCategoryClick(categoryId: number | null): void {
    this.selectedCategoryId.set(categoryId);
    this.brandList()?.reloadData(true);
  }

  onBrandClick(brand: IBrand): void {
    this.#router.navigate(['brands', brand.id], {
      relativeTo: this.#activatedRoute.parent,
      state: { brandName: brand.name }
    });
  }

  #loadCategories(): void {
    this.categoriesIsLoading.set(true);
    this.#categoriesService
      .getCategoriesList()
      .pipe(
        finalize(() => {
          this.categoriesIsLoading.set(false);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((categories: ICategory[]) => {
        this.categories.set(categories);
      });
  }
}
